import { Link } from 'react-router-dom';

import { warehouses } from 'constants/warehouses';

import { ProductType } from 'types/api/product.type';

import { useAppSelector } from 'redux/hooks';
import { RootState } from 'redux/store';

import { OrderButton } from 'pages/Catalogue/components/ListItem/components/OrderButton';

import { Button } from 'components/Button';
import { Headline } from 'components/Headline';
import { Image } from 'components/Image';
import { Notification } from 'components/Notification';
import { Sticker } from 'components/Sticker';
import { StickerSize } from 'components/Sticker/Sticker.styles';
import { QtySticker } from 'components/common/Qty/Qty';
import { SoldSticker } from 'components/common/SoldSticker/SoldSticker';

import { SelectionModal } from '../SelectionModal';
import { useProductItem } from './ListItem.hooks';
import * as S from './ListItem.styles';

type ListItemProps = {
  data: ProductType;
  title: string;
  desc: string;
  layout?: string;
  asSubProduct?: boolean;
  asSample?: boolean;
  notificationCount?: number;
  notificationUnRead?: boolean;
};

const pickTitleScale = (layout?: string) => {
  switch (layout) {
    case 'grid':
      return 'xl';
    case 'grid2':
      return 'lg';
    default:
      return 'xl2';
  }
};

const pickStickerSize = (layout?: string): StickerSize => {
  switch (layout) {
    case 'grid':
      return 'small';
    case 'grid2':
      return 'xsmall';
    default:
      return 'default';
  }
};

export function ListItem(props: ListItemProps): React.ReactElement {
  const {
    data,
    title,
    desc,
    layout,
    asSubProduct = false,
    asSample = false,
    notificationCount = 0,
    notificationUnRead = true,
  } = props;
  const isRemoved = !data?.active;
  const qty = data.quantity ?? 0;
  const { warehouse } = useAppSelector((state: RootState) => state.settings);
  const { code } = warehouse;
  const isSimpleCatalogue = code === warehouses[0].code;
  const qtyBiggerThanZero = qty > 0;
  const {
    image,
    isLoading,
    isModalVisible,
    isModalVisibleSet,
    t,
    path,
    isAvailable,
    quickOrder,
    routes,
    routeParams,
  } = useProductItem(data, asSubProduct);

  return (
    <S.Wrapper layout={layout}>
      <S.ImageWrapper
        layout={layout}
        isImageEmpty={isLoading ? false : !image || !image.length}
      >
        <Link to={path}>
          <Image
            // opacity={data?.active ? '1' : '0.35'}
            source={image}
            alt="product"
            className="product-image-wrapper"
          >
            <S.TopStickers>
              {notificationCount && notificationCount > 0 ? (
                <Notification
                  amount={notificationCount}
                  notificationColor="redSecondary"
                  data={data}
                  notificationUnRead={notificationUnRead}
                />
              ) : null}
              {data.onlyPrice === 'true' && (
                <S.OnlyPrice>
                  <Sticker size={pickStickerSize(layout)}>{t('product.onlyPrice')}</Sticker>
                </S.OnlyPrice>
              )}
            </S.TopStickers>
            {isRemoved ? (
              <S.InactiveLayer>
                <Sticker size={pickStickerSize(layout)}>{t('product.cancelled')}</Sticker>
              </S.InactiveLayer>
            ) : undefined}
            {!qtyBiggerThanZero && !isSimpleCatalogue && (
              <S.OutOfStock>
                <Sticker size={pickStickerSize(layout)}>{t('product.outOfStock')}</Sticker>
              </S.OutOfStock>
            )}
          </Image>
          <SoldSticker
            soldQty={data.sold}
            isRemoved={isRemoved}
            size={pickStickerSize(layout)}
            style={{ position: 'absolute', bottom: 0, left: 0 }}
          />
          {qtyBiggerThanZero && (
            <S.QtyStickerWrapper>
              <QtySticker qty={qty} size={pickStickerSize(layout)} />
            </S.QtyStickerWrapper>
          )}
        </Link>
      </S.ImageWrapper>
      <S.Titles>
        {title && (
          <Button
            asLink
            to={path}
            scale={pickTitleScale(layout)}
            color="blackLight"
            motive="productTitle"
            textTransform="uppercase"
            textAlign="center"
            whiteSpace="normal"
          >
            {asSample ? '*' : ''}
            {title}
          </Button>
        )}
        {desc && (
          <Headline
            scale="xs"
            color="title"
            fontWeight="light"
            fontFamily="LFT Zeno"
            textTransform="uppercase"
            textAlign="center"
            isWrapped={true}
          >
            {desc}
          </Headline>
        )}
      </S.Titles>
      <S.CTA isCentered={asSubProduct}>
        {asSubProduct ? (
          <>
            {data?.colorCode ? (
              <OrderButton
                colorCode={data?.colorCode}
                disabled={!isAvailable || !data?.active}
                subProductId={data.id}
                itemCode={data.itemCode}
                mainFabricCode={data.mainFabricCode}
              />
            ) : undefined}

            <S.HeartIcon
              iconLeft="FiHeart"
              color="red"
              onClick={() => isModalVisibleSet(true)}
            />
          </>
        ) : (
          <>
            <S.StyledButton
              fontFamily="LFT Zeno"
              fontWeight="light"
              textTransform="isFirstLetterUppercase"
              scale="xs"
              iconLeft="FiExternalLink"
              motive="addSecondary"
              icoarea="2"
              asLink
              to={routes.colorsList(routeParams)}
              withSeparator={true}
            >
              {t('product.viewMore')}
            </S.StyledButton>
            <S.StyledButton
              fontFamily="LFT Zeno"
              fontWeight="light"
              textTransform="isFirstLetterUppercase"
              scale="xs"
              iconLeft="FiShoppingBag"
              motive="addSecondary"
              icoarea="1.5"
              onClick={() => {
                quickOrder();
              }}
            >
              {t('product.quickOrder')}
            </S.StyledButton>
          </>
        )}
      </S.CTA>
      {isModalVisible ? (
        <SelectionModal productId={data.id} onClose={() => isModalVisibleSet(false)} />
      ) : undefined}
    </S.Wrapper>
  );
}
